import type { WorksheetGrade } from "@bindery/shared/contracts/worksheet";
import { useMutation, useQuery } from "convex/react";
import { useCallback, useState } from "react";
import { createWorksheetFor } from "./create-worksheet";
import {
  forConvex,
  worksheetsApi,
  type WorksheetGradeOutcome,
} from "./worksheets-api";

/** `undefined` while loading, `null` when signed out or bound to another reader. */
export function useWorksheets(readerId: string | null) {
  return useQuery(
    worksheetsApi.listForReader,
    readerId === null ? "skip" : { expectedReaderId: readerId },
  );
}

/** `null` for a sheet that is not the signed-in reader's. */
export function useWorksheet(worksheetId: string) {
  return useQuery(worksheetsApi.get, { worksheetId });
}

export function useCreateWorksheet(readerId: string) {
  const store = useMutation(worksheetsApi.create);
  return useCallback(
    () => createWorksheetFor(readerId, (args) => store(forConvex(args))),
    [readerId, store],
  );
}

/** Grades with the QR token when one is given, else with the signed-in session. */
export function useGradeWorksheet(
  worksheetId: string,
  token?: string,
): (grade: WorksheetGrade) => Promise<WorksheetGradeOutcome> {
  const gradeSignedIn = useMutation(worksheetsApi.grade);
  const gradeWithToken = useMutation(worksheetsApi.gradeWithToken);
  return useCallback(
    (grade) =>
      token === undefined
        ? gradeSignedIn({ worksheetId, grade: forConvex(grade) })
        : gradeWithToken({ worksheetId, grade: forConvex(grade), token }),
    [worksheetId, token, gradeSignedIn, gradeWithToken],
  );
}

/** Page state only: a name an adult types for a child is never persisted or sent. */
export function usePrintName(defaultName: string) {
  const [typed, setTyped] = useState<string | null>(null);
  return [typed ?? defaultName, setTyped] as const;
}
